// Chef-Statistik — reine, I/O-freie Auswertungslogik (W9.6). Die DB-/Zeit-Aggregation (Stempel→Sekunden über
// @wf/shared/time, B-5) liefert der Aufrufer; hier nur die kennzahlbildende Mathematik. Geld in ganzen Cent.

/** Ein verplanter Belegungs-Slot (Terminplanung) in Minuten ab Mitternacht. */
export interface ScheduledSlot {
  startMin: number
  endMin: number
}

export interface PeakHours {
  /** Termine je Tagesstunde 0..23 (Auslastung). */
  buckets: number[]
  /** Stunde mit den meisten Terminen (null, wenn keine). */
  busiestHour: number | null
  busiestCount: number
}

/**
 * Auslastung je Tagesstunde aus den verplanten Slots: jeder Slot zählt für jede angebrochene Stunde
 * [floor(start/60) .. ceil(end/60)). Liefert die Stundeneimer + die Spitzenstunde. (Alt-Parität stats().)
 */
export function peakHours(slots: readonly ScheduledSlot[]): PeakHours {
  const buckets = new Array<number>(24).fill(0)
  for (const s of slots) {
    const h0 = Math.floor(s.startMin / 60)
    const h1 = Math.ceil(s.endMin / 60)
    for (let h = h0; h < h1 && h < 24; h++) if (h >= 0) buckets[h] = (buckets[h] ?? 0) + 1
  }
  let busiestHour: number | null = null
  let busiestCount = 0
  buckets.forEach((c, h) => {
    if (c > busiestCount) {
      busiestCount = c
      busiestHour = h
    }
  })
  return { buckets, busiestHour, busiestCount }
}

/**
 * Auslastung der Arbeitsplätze in Prozent: verplante Slot-Minuten / Kapazität, mit
 * Kapazität = bays × Öffnungsminuten/Tag × Tage. Zählt ALLE terminierten Slots (auch ohne
 * Bay-Zuordnung — ein Termin bindet Kapazität, egal ob schon ein Platz gewählt ist; Parität
 * zu peakHours/totalScheduled). Ganzzahlig gerundet, kann >100 sein (Überbuchung);
 * null ohne Kapazität (bays/Öffnungsfenster/Tage = 0 → kein /0).
 */
export function bayUtilization(
  slots: readonly ScheduledSlot[],
  bays: number,
  openMinutesPerDay: number,
  days: number,
): number | null {
  const capacity = bays * openMinutesPerDay * days
  if (capacity <= 0) return null
  let booked = 0
  for (const s of slots) booked += Math.max(0, s.endMin - s.startMin)
  return Math.round((booked / capacity) * 100)
}

/** Ein Auftrag fürs Geschäftskennzahlen-Aggregat. valueCents = Lohn + Σ Teile + Σ Positionen (ganze Cent). */
export interface BusinessOrder {
  status: string
  estimateStatus: string
  valueCents: number
  /** Annahme- bzw. letzter Änderungszeitpunkt (epoch ms) — für die Durchlaufzeit. */
  createdAtMs: number
  updatedAtMs: number
}

export interface BusinessMetrics {
  ordersTaken: number
  ordersDone: number
  revenueCents: number
  revenueDoneCents: number
  avgOrderCents: number
  estApproved: number
  estDeclined: number
  /** Ø Durchlaufzeit Annahme→Abholung in Tagen (1 Nachkommastelle), null ohne abgeholte Aufträge. */
  avgLeadDays: number | null
}

/** Geschäfts-/Umsatzkennzahlen über die Aufträge des Zeitraums (Alt-Parität stats().business). */
export function businessMetrics(orders: readonly BusinessOrder[]): BusinessMetrics {
  let revenue = 0
  let doneCount = 0
  let doneRevenue = 0
  let leadSum = 0
  let leadN = 0
  let estApproved = 0
  let estDeclined = 0
  for (const o of orders) {
    revenue += o.valueCents
    if (o.status === 'fertig' || o.status === 'abgeholt') {
      doneCount += 1
      doneRevenue += o.valueCents
    }
    if (o.status === 'abgeholt') {
      const lead = o.updatedAtMs - o.createdAtMs // Annahme → Abholung
      if (lead >= 0) {
        leadSum += lead
        leadN += 1
      }
    }
    if (o.estimateStatus === 'approved') estApproved += 1
    else if (o.estimateStatus === 'declined') estDeclined += 1
  }
  const ordersTaken = orders.length
  return {
    ordersTaken,
    ordersDone: doneCount,
    revenueCents: revenue,
    revenueDoneCents: doneRevenue,
    avgOrderCents: doneCount ? Math.round(doneRevenue / doneCount) : ordersTaken ? Math.round(revenue / ordersTaken) : 0,
    estApproved,
    estDeclined,
    avgLeadDays: leadN ? Math.round((leadSum / leadN / 86_400_000) * 10) / 10 : null,
  }
}

/** Produktivität in Prozent: produktive Job-Zeit / Anwesenheit. null, wenn keine Anwesenheit (kein /0). */
export function productivityPercent(jobSeconds: number, attendanceSeconds: number): number | null {
  return attendanceSeconds > 0 ? Math.round((jobSeconds / attendanceSeconds) * 100) : null
}

/** Mittelwert der vorhandenen Produktivitätswerte (null ignoriert); null, wenn keiner vorhanden. */
export function averageProductivity(values: readonly (number | null)[]): number | null {
  const present = values.filter((v): v is number => v != null)
  return present.length ? Math.round(present.reduce((a, b) => a + b, 0) / present.length) : null
}
